import type { SearchAccess, SearchResult } from "./searchTypes";
import { matchesQuery, take } from "./searchHelpers";

interface PageEntry {
  id: string;
  title: string;
  subtitle: string;
  keywords: string[];
  path: string;
  visible: (access: SearchAccess) => boolean;
}

const PAGE_ENTRIES: PageEntry[] = [
  {
    id: "page-dashboard",
    title: "Dashboard",
    subtitle: "Site overview",
    keywords: ["dashboard", "overview", "home", "stats"],
    path: "/dashboard",
    visible: (a) => a.isFullAdmin || a.canViewCouples,
  },
  {
    id: "page-profile",
    title: "Update Your Profile",
    subtitle: "Your couple profile",
    keywords: ["profile", "update", "couple", "account"],
    path: "/update-couple",
    visible: (a) => a.isFullAdmin || a.canViewCouples || a.hasCoupleProfile,
  },
  {
    id: "page-participated",
    title: "Participated In Retreats",
    subtitle: "Retreats you have attended",
    keywords: ["participated", "history", "attended"],
    path: "/participated-in-retreats",
    visible: (a) => a.isFullAdmin || a.canViewCouples || a.hasCoupleProfile,
  },
  {
    id: "page-create-ticket",
    title: "Create Ticket",
    subtitle: "Submit a help request",
    keywords: ["ticket", "create ticket", "help", "support"],
    path: "/create-ticket",
    visible: () => true,
  },
  {
    id: "page-upcoming",
    title: "Upcoming Retreats",
    subtitle: "Public upcoming retreat list",
    keywords: ["upcoming", "incoming", "future retreats"],
    path: "/upcoming-retreats",
    visible: (a) => a.canViewUpcoming,
  },
  {
    id: "page-couples",
    title: "Manage Couples",
    subtitle: "Couples directory",
    keywords: ["couples", "manage couples", "directory"],
    path: "/manageCouples",
    visible: (a) => a.canViewCouples,
  },
  {
    id: "page-retreats",
    title: "Manage Retreats",
    subtitle: "Retreat lifecycle dashboard",
    keywords: ["manage retreats", "retreats", "pipeline"],
    path: "/manageRetreat",
    visible: (a) => a.canManageRetreats,
  },
  {
    id: "page-search",
    title: "Advanced Search",
    subtitle: "Couples and retreats filters",
    keywords: ["advanced search", "search data"],
    path: "/search_data",
    visible: (a) => a.isFullAdmin,
  },
  {
    id: "page-quick-report",
    title: "Quick Reports",
    subtitle: "Saved report shortcuts",
    keywords: ["quick reports", "reports"],
    path: "/quickReport",
    visible: (a) => a.isFullAdmin,
  },
  {
    id: "page-closures",
    title: "Retreat Closure",
    subtitle: "Closing checklist and mail",
    keywords: ["closure", "closing", "close retreat"],
    path: "/retreat-closure-list",
    visible: (a) => a.canViewClosures,
  },
  {
    id: "page-tickets",
    title: "Tickets",
    subtitle: "Help ticket inbox",
    keywords: ["tickets", "inbox", "help desk"],
    path: "/tickets",
    visible: (a) => a.canViewTickets,
  },
  {
    id: "page-admin-dashboard",
    title: "Admin Dashboard",
    subtitle: "Admins, committee, and retreat sub admins",
    keywords: ["admin dashboard", "steering", "super admin"],
    path: "/admin-dashboard",
    visible: (a) => a.canViewAdminDashboard,
  },
  {
    id: "page-parish",
    title: "Parishes",
    subtitle: "Master Data",
    keywords: ["parish", "parishes", "master data"],
    path: "/parish",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-service",
    title: "Services",
    subtitle: "Master Data",
    keywords: ["service", "services", "master data"],
    path: "/service",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-roles",
    title: "Roles",
    subtitle: "Master Data",
    keywords: ["roles", "service role", "master data"],
    path: "/roles",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-talks",
    title: "Talks",
    subtitle: "Master Data",
    keywords: ["talks", "talk", "master data"],
    path: "/talks",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-parish-categories",
    title: "Parish Categories",
    subtitle: "Master Data",
    keywords: ["parish categories", "categories", "master data"],
    path: "/parishCategories",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-deaneries",
    title: "Deaneries",
    subtitle: "Master Data",
    keywords: ["deanery", "deaneries", "diocese", "parish mapping", "master data"],
    path: "/deaneries",
    visible: (a) => a.canViewMasterData,
  },
  {
    id: "page-sample-content",
    title: "Sample Content",
    subtitle: "Master Data",
    keywords: ["sample content", "master data"],
    path: "/sample-content",
    visible: (a) => a.canViewMasterData,
  },
];

export function searchPages(
  query: string,
  access: SearchAccess,
): SearchResult[] {
  const hits = PAGE_ENTRIES.filter((page) => page.visible(access)).filter(
    (page) =>
      matchesQuery(query, page.title, page.subtitle, ...page.keywords),
  );

  return take(
    hits.map((page) => ({
      id: page.id,
      category: "pages" as const,
      title: page.title,
      subtitle: page.subtitle,
      path: page.path,
    })),
  );
}
